import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useReportRuns} from '../hooks/useReportRuns';
import {useReportRunsFilter} from '../hooks/useReportRunsFilter';
import {useReportRunsSearch} from '../hooks/useReportRunsSearch';
import {ReportRun} from '../types';

// FNP-918: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody + the app-local PaginationControls) to the shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows — but it structurally
// could not have sorting, token pagination, or the loading/empty/error states,
// because all three come from the shared component (§3z).
//
// ⚠️ NO ROW SELECTION, and that is deliberate rather than forgotten (§3).
// A report run is an IMMUTABLE record of a job that was executed: the API
// exposes list/get/create and no delete or update. There is no batch operation
// for a checkbox to drive, so adding a select column would be an affordance
// with nothing behind it. This note exists because "no selection" and
// "selection was forgotten" look identical in a diff, and the next auditor
// would otherwise re-open the question.
//
// ⚠️ `src/components/PaginationControls` and `src/hooks/usePagination` are NOT
// deleted here. Other modules still import them; they come out in a later wave.

export const RecordsTable = () => {

    const navigate = useNavigate();

    const {filters} = useReportRunsFilter();

    const {
        columns,
        // The loaded window BEFORE filtering — the denominator the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = useReportRuns(filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = useReportRunsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // TWO empty states that mean OPPOSITE things (§3). `noRecordsText` is a
    // single string and cannot express both: "no report runs yet" for a search
    // that matched nothing sends an operator hunting for a bug.
    const noRecordsText = keyword.trim() !== ''
        ? I18n.get('No report runs match this search')
        : I18n.get('No report runs yet');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and renders the rows-per-page
              * dropdown, which token pagination cannot honour — there is no page
              * to resize and no total to divide (§9). The previous table passed
              * `limit={10}` and a no-op handler to satisfy it, so the dropdown
              * rendered and did nothing.
              *
              * TableSearchForm is also the CLEARABLE one: its clear control
              * resets both the input and the module-scoped keyword store, so the
              * list cannot stay narrowed while the box looks empty.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert
                    color='red'
                    title={I18n.get('Could not load report runs')}
                >
                    {I18n.get(
                        'The billing API did not return the report run list. Try '
                        + 'Reload; if it persists the API may be unavailable in this '
                        + 'environment.',
                    )}
                </Alert>
            )}

            <MantineDataTable<ReportRun>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the
                // same scope the keyword filter works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}: {record: ReportRun}) =>
                    navigate(`/report-runs/${encodeURIComponent(record.id)}`)}
            />

            {/*
              * A SIBLING of the table, not a table prop: the table renders rows,
              * the footer answers "have I seen everything?" (§9).
              *
              * `loadedCount` is the rows in the browser — NEVER a dataset total.
              * `filterTerm` must be passed whenever a keyword is active, or a
              * filtered view silently reports the unfiltered state.
              */}
            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'report run'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
